import React, { useEffect } from "react";
import { useMap } from "./Context";
import { transform } from "ol/proj";
import { TileWMS } from "ol/source";
import type { AppDispatch } from "@store/store";
import { setAmenityField } from "@modules/stepper/redux/amenitesSlice";
import { setAmenityROWField } from "@modules/stepper-row/redux/amenitesROWSlice";
import type { RootReducerType } from "@store/store";
import { useSelector, useDispatch } from "react-redux";
import {
  multiplePIUListThunk,
  getSelectedStretchThunk,
} from "@modules/stepper-row/redux/thunk/amenitiesThunk";
import { INDIAN_STATE_CODES } from "./constant";
import { setSearchCoordinates, setSelectedStretch } from "@gis/redux/gisSlice";
import { setCoordinateSite } from "@modules/stepper-row/redux/stepperROWSlice";
import { toast } from "react-toastify";
import { fetchFeatureInfoThunk } from "@gis/redux/gisThunk";

interface MapClickCoordinatesProps {
  onClick: (
    coordinates: { latitude: number; longitude: number },
    chainage?: number,
  ) => void;
}

const MapClickCoordinates: React.FC<MapClickCoordinatesProps> = ({
  onClick,
}) => {
  const { map } = useMap();
  const dispatch = useDispatch<AppDispatch>();
  const { applicationType } = useSelector(
    (state: RootReducerType) => state.dashboardSlice,
  );
  const {
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    national_highway_number,
  } = useSelector((state: RootReducerType) => state.amenitiesROW);
  const { coordinateSite } = useSelector(
    (state: RootReducerType) => state.stepperROW,
  );

  const fetchMultiplePIUList = async () => {
    if (
      start_latitude !== 0 &&
      start_longitude !== 0 &&
      end_latitude !== 0 &&
      end_longitude !== 0
    ) {
      const multiplePIUListPayload = {
        start_latitude: start_latitude,
        start_longitude: start_longitude,
        end_latitude: end_latitude,
        end_longitude: end_longitude,
      };

      const selectedStretch = {
        start_lat: start_latitude,
        start_long: start_longitude,
        end_lat: end_latitude,
        end_long: end_longitude,
      };

      const multiplePIUResponse = await dispatch(
        multiplePIUListThunk(multiplePIUListPayload),
      ).unwrap();
      if (multiplePIUResponse?.data?.ros?.length > 1) {
        toast.warning(
          "Selected points are in different jurisdictions. Please select points within the same jurisdiction.",
          {
            // autoClose: false,
            // closeOnClick: false,
            draggable: false,
          },
        );
        return;
      }
      if (multiplePIUResponse?.data?.ros > 0) {
        dispatch(
          setAmenityROWField({
            name: "ro_id",
            value: Number(multiplePIUResponse?.data?.ros?.[0]) || 1,
          }),
        );
      }
      if (multiplePIUResponse?.data?.pius > 0) {
        dispatch(
          setAmenityROWField({
            name: "piu_ids",
            value: multiplePIUResponse?.data?.pius?.map((piu: string) =>
              Number(piu),
            ) || [0],
          }),
        );
      }
      const getSelectedStretchResponse = await dispatch(
        getSelectedStretchThunk(selectedStretch),
      ).unwrap();
      if (getSelectedStretchResponse?.data?.length > 0) {
        dispatch(setSelectedStretch(getSelectedStretchResponse?.data || []));
      }
    }
  };

  useEffect(() => {
    fetchMultiplePIUList();
  }, [start_latitude, start_longitude, end_latitude, end_longitude]);

  useEffect(() => {
    if (!map) return;

    const handleMapClick = async (event: any) => {
      const clickedCoordinate = event.coordinate;
      // Transform the coordinates from EPSG:3857 to EPSG:4326 (latitude/longitude)
      const [longitude, latitude] = transform(
        clickedCoordinate,
        "EPSG:3857",
        "EPSG:4326",
      );

      const view = map.getView();
      const viewResolution = view.getResolution() ?? 1;
      const projection = view.getProjection();
      const layers = map.getLayers().getArray();
      const wmsLayers = layers.filter(
        (layer: any) => layer.getSource() instanceof TileWMS,
      );

      if (wmsLayers.length === 0) return;

      // const clickedCoordinate = event.coordinate;
      let selectedFeature: any | null = null;

      for (const layer of wmsLayers) {
        const source = (layer as any).getSource() as TileWMS;
        const layerName = source.getParams().LAYERS;

        const url = source.getFeatureInfoUrl(
          clickedCoordinate,
          viewResolution,
          projection,
          {
            INFO_FORMAT: "application/json",
            FEATURE_COUNT: 1,
          },
        );
        if (url) {
          try {
            const data = await dispatch(fetchFeatureInfoThunk(url)).unwrap();

            if (data.features.length > 0) {
              // Find the first feature that is a Point or MultiPoint
              const pointFeature = data.features.find(
                (feature: any) =>
                  feature.geometry.type === "MultiLineString" ||
                  feature.geometry.type === "MultiPolygon" ||
                  feature.geometry.type === "MultiPoint",
              );
              let initChainageKm = "";
              let initChainageM = "";
              // console.log("pointFeature", pointFeature)
              // if (
              //   pointFeature?.properties?.agency_id === null ||
              //   pointFeature?.properties?.agency_id === undefined
              // ) {
              //   toast.error(
              //     "Selected point lies in no jurisdiction. Please select a valid point.",
              //     {
              //       //  autoClose: false,
              //       //       closeOnClick: false,
              //       draggable: false,
              //     },
              //   );
              //   return;
              // }

              if (
                typeof pointFeature?.properties?.ChainageID === "string" &&
                pointFeature?.properties?.ChainageID.includes("+")
              ) {
                [initChainageKm, initChainageM] =
                  pointFeature?.properties?.ChainageID.split("+").map(
                    (x: any) => x.trim(),
                  );
              }
              if (pointFeature) {
                const stateName = pointFeature?.properties?.state_ut || pointFeature?.properties?.StateName;
                const stateCodeObj = INDIAN_STATE_CODES?.find(
                  (item: any) =>
                    item?.label?.toLowerCase() === stateName?.toLowerCase(),
                );
                const stateCode = stateCodeObj ? stateCodeObj.short : "";
                dispatch(setSearchCoordinates({ latitude: 0, longitude: 0 }));
                if (applicationType === 1) {
                  dispatch(
                    setAmenityField({
                      name: "district",
                      value:
                        pointFeature?.properties?.DISTRICT ||
                        pointFeature?.properties?.District ||
                        "",
                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "state",
                      value:
                        pointFeature?.properties?.state_ut ||
                        pointFeature?.properties?.StateName ||
                        pointFeature?.properties?.State,

                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "state_code",
                      value: stateCode || "",
                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "chainage_km",
                      value: initChainageKm,
                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "chainage_m",
                      value: initChainageM,
                    }),
                  );

                  dispatch(
                    setAmenityField({
                      name: "national_highway_number",
                      value: pointFeature?.properties?.road_name || "",
                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "address_1",
                      value: pointFeature?.properties?.closest_te || "",
                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "authority_id",
                      value: pointFeature?.properties?.agency_id || 0,
                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "ro_id",
                      value: pointFeature?.properties?.ro_id_final || 0,
                    }),
                  );
                  dispatch(
                    setAmenityField({
                      name: "piu_id",
                      value: pointFeature?.properties?.piu_id_final || 0,
                    }),
                  );
                }

                if (applicationType === 2) {
                  dispatch(
                    setAmenityROWField({
                      name: "authority_id",
                      value: pointFeature?.properties?.agency_id || 0,
                    }),
                  );

                  dispatch(
                    setAmenityROWField({
                      name: "district",
                      value:
                        pointFeature?.properties?.DISTRICT ||
                        pointFeature?.properties?.District ||
                        "",
                    }),
                  );
                  dispatch(
                    setAmenityROWField({
                      name: "state",
                      value:
                        pointFeature?.properties?.state_ut ||
                        pointFeature?.properties?.StateName ||
                        pointFeature?.properties?.State,
                    }),
                  );
                  dispatch(
                    setAmenityROWField({
                      name: "state_code",
                      value: stateCode || "",
                    }),
                  );
                  dispatch(
                    setAmenityROWField({
                      name: "chainage",
                      value: pointFeature?.properties?.ChainageID,
                    }),
                  );

                  dispatch(
                    setAmenityROWField({
                      name: "national_highway_number",
                      value: pointFeature?.properties?.road_name || "",
                    }),
                  );
                  dispatch(
                    setAmenityROWField({
                      name: "address_1",
                      value: pointFeature?.properties?.closest_te || "",
                    }),
                  );
                  // if (coordinateSite === "end") {
                  //   if (
                  //     national_highway_number !==
                  //     pointFeature?.properties?.road_name
                  //   ) {
                  //     toast.warning(
                  //       "Selected point does not lie on the same National Highway as the start point. Please select a valid point.",
                  //       {
                  //         // autoClose: false,
                  //         // closeOnClick: false,
                  //         draggable: false,
                  //       },
                  //     );
                  //     return;
                  //   }
                  // }
                  dispatch(
                    setAmenityROWField({
                      name: "ro_id",
                      value: Number(pointFeature?.properties?.ro_id_final) || 0,
                    }),
                  );
                  dispatch(
                    setAmenityROWField({
                      name: "piu_id",
                      value: pointFeature?.properties?.piu_id_final || 0,
                    }),
                  );
                  dispatch(
                    setAmenityROWField({
                      name: "piu_ids",
                      value: [
                        Number(pointFeature?.properties?.piu_id_final) || 0,
                      ],
                    }),
                  );
                }

                selectedFeature = {
                  properties: pointFeature.properties,
                  geometry: pointFeature.geometry,
                };
                break;
              }
            }
          } catch (error) {
            console.error("Error fetching feature info:", error);
          }
        }
      }

      // Call the onClick callback with the latitude and longitude
      const chainage = selectedFeature?.properties?.ChainageID || null;
      onClick({ latitude, longitude }, chainage);
    };

    // Add the click event listener to the map
    map.on("singleclick", handleMapClick);

    // Cleanup the event listener when the component is unmounted
    return () => {
      map.un("singleclick", handleMapClick);
    };
  }, [map, onClick]);

  return null; // This component doesn't render anything
};

export default MapClickCoordinates;
